import { Sparkles, Loader2, Languages, Wand2, ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { cn } from '@/lib/cn';
import {
  AI_PRESETS,
  aiGenerate,
  aiGenerateStream,
  getAiCatalogue,
  type AiActionKey,
  type AiActionMeta,
  type AiFormat,
  type AiGenerateOptions,
} from '@/lib/ai';

export interface AIActionButtonProps {
  /**
   * Preset describing which actions to show. Use the predefined ones
   * (richText, title, excerpt, seoTitle...) or pass `actions` to override.
   */
  preset?: keyof typeof AI_PRESETS;
  actions?: AiActionKey[];

  /**
   * Function returning the current text to operate on (called on click).
   * Allows the parent to expose live state without re-creating the button.
   */
  getInput: () => string;

  /**
   * Called with the generated text. The format hint matches the action's
   * output (`plain`, `html`, `json`). Parents decide how to apply it.
   */
  onApply: (text: string, meta: { action: AiActionKey; format: AiFormat }) => void;

  /**
   * Extra context passed to the LLM (page title, locale, target language...).
   */
  options?: AiGenerateOptions;

  /**
   * Languages offered in the Translate submenu. Defaults to ES/EN/FR/PT/DE.
   */
  languages?: { code: string; label: string }[];

  /**
   * Stream tokens as they arrive via SSE. Calls `onApply` with the partial
   * text on every chunk so the field updates live. Defaults to false because
   * many use cases (SEO title, alt text) benefit from a single atomic apply.
   */
  stream?: boolean;

  variant?: 'ghost' | 'outline' | 'secondary';
  size?: 'sm' | 'icon' | 'default';
  align?: 'start' | 'end' | 'center';
  className?: string;
  iconOnly?: boolean;
  disabled?: boolean;
  label?: string;
}

const DEFAULT_LANGUAGES = [
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'pt', label: 'Português' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
];

export function AIActionButton({
  preset = 'plainText',
  actions: actionsOverride,
  getInput,
  onApply,
  options,
  languages = DEFAULT_LANGUAGES,
  variant = 'ghost',
  size = 'sm',
  align = 'end',
  className,
  iconOnly = false,
  disabled = false,
  label = 'AI',
  stream = false,
}: AIActionButtonProps) {
  const [loading, setLoading] = useState<AiActionKey | null>(null);
  const [catalogue, setCatalogue] = useState<AiActionMeta[]>([]);
  const [open, setOpen] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let active = true;
    getAiCatalogue()
      .then((c) => {
        if (active) setCatalogue(c.actions);
      })
      .catch(() => {
        // graceful: dropdown still shows preset labels from the constant map
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (warningTimer.current) clearTimeout(warningTimer.current);
    };
  }, []);

  const actionKeys: AiActionKey[] = useMemo(
    () => actionsOverride ?? AI_PRESETS[preset] ?? AI_PRESETS.plainText!,
    [actionsOverride, preset],
  );

  const items = useMemo(() => {
    const byKey = new Map(catalogue.map((a) => [a.key, a]));
    return actionKeys
      .map<AiActionMeta | null>((key) => byKey.get(key) ?? FALLBACK_LABELS[key] ?? null)
      .filter((x): x is AiActionMeta => x !== null);
  }, [actionKeys, catalogue]);

  const hasTranslate = items.some((i) => i.key === 'translate');

  const showWarning = (msg: string) => {
    setWarning(msg);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    warningTimer.current = setTimeout(() => setWarning(null), 2500);
  };

  const run = useCallback(
    async (action: AiActionKey, extra: AiGenerateOptions = {}) => {
      const input = (getInput() ?? '').trim();
      if (!input) {
        showWarning('No hay texto para procesar');
        return;
      }
      const meta = items.find((i) => i.key === action) ?? FALLBACK_LABELS[action];
      const format: AiFormat = meta?.format ?? 'plain';
      setOpen(false);
      setLoading(action);
      try {
        if (stream) {
          await aiGenerateStream(action, input, { ...options, ...extra }, {
            onToken: (_chunk, full) => {
              onApply(full, { action, format });
            },
            onError: (msg) => showWarning(msg),
          });
        } else {
          const result = await aiGenerate(action, input, { ...options, ...extra });
          if (!result.text) {
            showWarning('Respuesta vacía del modelo');
            return;
          }
          onApply(result.text, { action, format });
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Error AI';
        showWarning(msg);
      } finally {
        setLoading(null);
      }
    },
    [getInput, items, onApply, options, stream],
  );

  return (
    <div className={cn('relative inline-flex', className)}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant={variant}
            size={size}
            disabled={disabled || loading !== null}
            className="gap-1.5 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
            aria-label="Asistente AI"
            title="Asistente AI"
          >
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            {!iconOnly && <span>{label}</span>}
            {!iconOnly && <ChevronDown className="h-3 w-3 opacity-60" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="min-w-[15rem]">
          <DropdownMenuLabel className="flex items-center gap-2 text-xs uppercase tracking-wide text-neutral-500">
            <Wand2 className="h-3 w-3" /> AI assistant
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {items
            .filter((i) => i.key !== 'translate')
            .map((item) => (
              <DropdownMenuItem
                key={item.key}
                onSelect={(e) => {
                  e.preventDefault();
                  void run(item.key);
                }}
                disabled={loading !== null}
              >
                {loading === item.key ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5 opacity-50" />
                )}
                {item.label}
              </DropdownMenuItem>
            ))}
          {hasTranslate && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Languages className="h-3.5 w-3.5 opacity-50" />
                  Traducir
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuContent className="min-w-[10rem]">
                    {languages.map((lang) => (
                      <DropdownMenuItem
                        key={lang.code}
                        onSelect={(e) => {
                          e.preventDefault();
                          void run('translate', { targetLocale: lang.code });
                        }}
                      >
                        {lang.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {warning && (
        <div
          role="status"
          className="absolute right-0 top-full z-50 mt-1 whitespace-nowrap rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800 shadow-sm"
        >
          {warning}
        </div>
      )}
    </div>
  );
}

/**
 * Local fallback labels so the dropdown shows something useful even if the
 * /ai/actions request hasn't finished yet (or failed offline).
 */
const FALLBACK_LABELS: Record<AiActionKey, AiActionMeta> = {
  improve: { key: 'improve', label: 'Mejorar redacción', format: 'plain' },
  shorten: { key: 'shorten', label: 'Acortar', format: 'plain' },
  expand: { key: 'expand', label: 'Ampliar', format: 'plain' },
  tone_formal: { key: 'tone_formal', label: 'Tono más formal', format: 'plain' },
  tone_casual: { key: 'tone_casual', label: 'Tono más cercano', format: 'plain' },
  fix_grammar: { key: 'fix_grammar', label: 'Corregir ortografía', format: 'plain' },
  translate: { key: 'translate', label: 'Traducir', format: 'plain' },
  seo_title: { key: 'seo_title', label: 'Generar SEO title', format: 'plain' },
  seo_description: { key: 'seo_description', label: 'Generar SEO description', format: 'plain' },
  excerpt: { key: 'excerpt', label: 'Generar extracto', format: 'plain' },
  title_suggest: { key: 'title_suggest', label: 'Sugerir título', format: 'plain' },
  alt_text: { key: 'alt_text', label: 'Generar alt', format: 'plain' },
  continue: { key: 'continue', label: 'Continuar escribiendo', format: 'plain' },
  rewrite_html: { key: 'rewrite_html', label: 'Reescribir (HTML)', format: 'html' },
  block_suggest: { key: 'block_suggest', label: 'Sugerir bloque', format: 'json' },
};
